import { inject, type MaybeRefOrGetter, type Ref, ref, toValue, watchEffect } from "vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";

/**
 * O endereço de onde mostrar um arquivo que já está guardado.
 *
 * ── POR QUE UM COMPOSABLE E NÃO UMA `src` MONTADA NA TELA ─────────────────
 *
 * Porque ninguém monta URL de arquivo na mão (I-S4): quem sabe onde os bytes moram
 * é o servidor, e a resposta muda com o driver de storage — uma URL assinada com
 * validade quando o bucket é objeto, um caminho quando é disco. Três telas
 * desenham o mesmo rosto e o mesmo logo, e três cópias desse cálculo seriam três
 * lugares para a próxima mudança esquecer.
 *
 * ── POR QUE PELA PORTA, E NÃO IMPORTANDO `features/files` ─────────────────
 *
 * Uma feature nunca importa outra, e o `UserMenu` nem feature é. A `UploadPort` já
 * é fornecida na raiz do app (`App.vue`), já tem `urlFor`, e já guarda em cache o
 * que resolveu — então seis linhas com o mesmo id custam uma consulta. É a mesma
 * porta que os widgets dinâmicos usam para exatamente isto.
 *
 * Sem porta fornecida — que é o caso das telas públicas, onde não há sessão —
 * devolve `null`, e quem desenha cai no que já desenhava. Uma falha na consulta
 * também: um rosto que não carregou vira a inicial do nome, nunca um erro na tela.
 */
export function useFileSrc(
	fileId: MaybeRefOrGetter<string | null | undefined>,
): Ref<string | null> {
	const uploads = inject(UPLOAD_PORT, null);
	const src = ref<string | null>(null);

	watchEffect(() => {
		const id = toValue(fileId);
		if (!uploads || !id) {
			src.value = null;
			return;
		}
		// O id do pedido em voo: se a foto mudar antes de a consulta voltar, a
		// resposta antiga é descartada em vez de sobrescrever a nova.
		const wanted = id;
		void uploads
			.urlFor(id)
			.then((file) => {
				// `urlFor` responde o arquivo inteiro; aqui só o endereço interessa.
				if (toValue(fileId) === wanted) src.value = file.url;
			})
			.catch(() => {
				if (toValue(fileId) === wanted) src.value = null;
			});
	});

	return src;
}
